import { useState } from "react";
import { Box, Table, Text } from "@radix-ui/themes";
import { observer } from "mobx-react";
import { AvatarPicker } from "../../components/modals/PersonModal/AvatarPicker";
import EditableField from "../../components/utilities/EditableField";
import { useStores } from "../../stores/useStores";
import { isSchoolFeedStandalone } from "../../utils/SchoolBlocksUtilities";
import WatsonApi from "../../backends/WatsonApi";
import NotificationManager from "../../components/notifications/NotificationManager";
import moment from "moment";

export const PublicInfo = observer(function PublicInfo(props: {
  userObj: IPersonBlock;
}) {
  const { userStore, organizationStore } = useStores();
  const [editingField, setEditingField] = useState<string | null>(null);
  const blockModel = props.userObj.blockModel;
  const canEditName = isSchoolFeedStandalone() && userStore.isMagicLinkUser;

  const handleFieldSave = async (field: string, value: string) => {
    const trimmed = value.trim();
    const oldValue = blockModel[field];

    props.userObj.setAttributes({
      blockModel: { ...blockModel, [field]: trimmed },
    });
    userStore.updateUser({ [field]: trimmed });
    setEditingField(null);

    try {
      const client = await WatsonApi();
      const result =
        await client.apis.organizations.organizations_users_partial_update(
          {
            organization_pk: organizationStore.organization.id,
            id: blockModel.id,
          },
          { requestBody: { id: blockModel.id, [field]: trimmed } },
        );
      const user = JSON.parse(result.data);
      userStore.updateUser(user);
      NotificationManager.success("Profile updated successfully.");
    } catch {
      NotificationManager.error("Error updating profile.");
      props.userObj.setAttributes({
        blockModel: { ...props.userObj.blockModel, [field]: oldValue },
      });
      userStore.updateUser({ [field]: oldValue });
    }
  };

  const renderNameField = (field: "fname" | "lname", label: string) => (
    <Table.Row>
      <Table.RowHeaderCell>
        <Text weight={"bold"}>{label}</Text>
      </Table.RowHeaderCell>
      <Table.Cell>
        <EditableField
          defaultValue={blockModel[field] || ""}
          isEditing={editingField === field}
          onEditStart={() => setEditingField(field)}
          onSave={(value) => handleFieldSave(field, value)}
          onCancel={() => setEditingField(null)}
          canEdit={canEditName}
          placeholder={`${label}...`}
        >
          <span>{blockModel[field] || `${label}...`}</span>
        </EditableField>
      </Table.Cell>
    </Table.Row>
  );

  return (
    <Box style={{ width: 500, maxWidth: "90vw" }}>
      <Table.Root variant={"surface"}>
        <Table.Body>
          <Table.Row>
            <Table.RowHeaderCell>
              <Text weight={"bold"}>Username</Text>
            </Table.RowHeaderCell>
            <Table.Cell>{blockModel.username}</Table.Cell>
          </Table.Row>
          {renderNameField("fname", "First Name")}
          {renderNameField("lname", "Last Name")}
          <Table.Row>
            <Table.RowHeaderCell>
              <Text weight={"bold"}>Avatar</Text>
            </Table.RowHeaderCell>
            <Table.Cell>
              <AvatarPicker blockObj={props.userObj} canEdit={true} />
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.RowHeaderCell>
              <Text weight={"bold"}>Last Modified</Text>
            </Table.RowHeaderCell>
            <Table.Cell>{moment(blockModel.modified).fromNow()}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table.Root>
    </Box>
  );
});
